// -----------------------------------------------------------------
// PortalPdfViewer — modal shell for Discovery + Launch portals.
//
// Renders the PDF inside the shared ``PdfJsViewer`` used by the admin
// HUB Files section, so admins + prospects get the same proven
// paginated experience: previous/next page navigation, page count and
// zoom controls. The modal shell adds the download button + close.
//
// URLs, permissions and backend endpoints are untouched — the
// component simply hands ``inlineUrl`` off to PdfJsViewer, which
// requests it directly (portal endpoints authenticate via the ?t=
// token in the query string, so no bearer/cookie plumbing is needed).
// -----------------------------------------------------------------
import { useState } from "react";
import { Download, FileText, X } from "lucide-react";
import PdfJsViewer from "@/components/files/PdfJsViewer";

export default function PortalPdfViewer({
  title,
  intro,
  inlineUrl,
  downloadUrl,
  viewButtonLabel = "View Agreement",
  downloadButtonLabel = "Download a Local Copy",
  modalTitle,
  testidPrefix,
}) {
  const [viewer, setViewer] = useState(false);

  return (
    <div data-testid={`${testidPrefix}-panel`}>
      {title && (
        <h3 className="font-display text-2xl md:text-3xl text-stone-950 mb-3" data-testid={`${testidPrefix}-title`}>
          {title}
        </h3>
      )}
      {intro && (
        <p className="text-stone-700 leading-relaxed mb-6 max-w-3xl">
          {intro}
        </p>
      )}

      <div className="flex flex-wrap gap-3">
        <button
          type="button"
          onClick={() => setViewer(true)}
          data-testid={`${testidPrefix}-view`}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-stone-950 text-[#dddd16] font-bold uppercase tracking-wider text-sm rounded-lg hover:bg-stone-800 transition-colors"
        >
          <FileText className="w-4 h-4" /> {viewButtonLabel}
        </button>
        <a
          href={downloadUrl}
          data-testid={`${testidPrefix}-download`}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-stone-950 border border-stone-300 font-bold uppercase tracking-wider text-sm rounded-lg hover:bg-stone-50 transition-colors"
        >
          <Download className="w-4 h-4" /> {downloadButtonLabel}
        </a>
      </div>

      {viewer && (
        <div className="fixed inset-0 z-[10050] bg-black/85 flex flex-col" data-testid={`${testidPrefix}-modal`}>
          <div className="flex items-center justify-between px-5 py-3 bg-stone-950 text-white gap-3">
            <div className="font-bold uppercase tracking-wider text-sm truncate">{modalTitle || title}</div>
            <div className="flex items-center gap-2 shrink-0">
              <a
                href={downloadUrl}
                target="_blank"
                rel="noreferrer"
                data-testid={`${testidPrefix}-modal-download`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#dddd16] text-stone-950 hover:bg-[#aaaa11] rounded-lg text-[10px] font-bold uppercase tracking-wider"
              >
                <Download className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Download</span>
              </a>
              <button
                onClick={() => setViewer(false)}
                data-testid={`${testidPrefix}-modal-close`}
                className="p-1.5 rounded hover:bg-white/10"
                aria-label="Close viewer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-auto bg-stone-50 flex items-start justify-center p-3 sm:p-4">
            <PdfJsViewer url={inlineUrl} />
          </div>
        </div>
      )}
    </div>
  );
}
